import React from "react";
import ReactCrop, {
    centerCrop,
    convertToPixelCrop,
    makeAspectCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

import { Button } from "components";
import { Row, CardImg, Col, Modal, ModalHeader } from "reactstrap";
import { notificationMessageActions } from "_actions";

import defaultImage from "assets/img/event_image_placeholder.jpg";
import defaultAvatar from "assets/img/event_image_placeholder.jpg";

import setCanvasPreview from "./setCanvasPreview";

const ASPECT_RATIO = 1;       // event banner aspect (wide)
const MIN_DIMENSION = 150;        // px minimum along smallest side
const MAX_IMAGE_BYTES = 7_500_000; // 7.5MB

class SquareProfileImageUpload extends React.Component {
    // Stable refs (never in state)
    fileInputRef = React.createRef();
    imgRef = React.createRef();
    previewCanvasRef = React.createRef();

    constructor(props) {
        super(props);
        this.state = {
            file: null,
            blob: null,
            croppedImageUrl: null,

            imagePreviewUrl: props.imageUrl
                ? props.imageUrl
                : defaultAvatar,

            index: props.index !== undefined ? props.index : null,

            // cropping state
            src: null,     // dataURL for cropper
            imgSrc: "",    // (kept for compatibility with older code)
            crop: { aspect: ASPECT_RATIO, width: 50, x: 0, y: 0 },

            error: "",
            cropOpen: false,
        };
        this.closeModal = this.closeModal.bind(this)
    }

    // ---------- helpers ----------
    readFileAsDataURL(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = () => reject(new Error("Failed to read file"));
            reader.onload = () => resolve(String(reader.result || ""));
            reader.readAsDataURL(file);
        });
    }

    ensureMinDimensions(dataUrl, minW, minH) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                if (img.naturalWidth >= minW && img.naturalHeight >= minH) resolve();
                else reject(new Error(`Image must be at least ${minW} x ${minH} pixels.`));
            };
            img.onerror = () => reject(new Error("Unable to load image"));
            img.src = dataUrl;
        });
    }

    closeModal() {
        this.setState({
            croppedImageUrl: null,
            imgSrc: null,
        })
    }


    canvasToBlob(canvas, type = "image/png", quality = 0.92) {
        return new Promise((resolve, reject) => {
            try {
                canvas.toBlob((blob) => {
                    if (blob) resolve(blob);
                    else reject(new Error("canvas.toBlob() returned null"));
                }, type, quality);
            } catch (e) {
                reject(e);
            }
        });
    }

    // ---------- actions ----------
    handleClick = () => {
        this.fileInputRef.current?.click();
        this.setState({ cropOpen: true });
    };

    onSelectFile = async (e) => {
        const file = e?.target?.files?.[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            this.props.dispatch(
                notificationMessageActions.addNotificationToQueue(
                    "Please select a JPG or PNG image.",
                    2
                )
            );
            return;
        }
        if (file.size > MAX_IMAGE_BYTES) {
            this.props.dispatch(
                notificationMessageActions.addNotificationToQueue(
                    "Your image must be less than 7.5 MB.",
                    2
                )
            );
            return;
        }

        try {
            const dataUrl = await this.readFileAsDataURL(file);
            await this.ensureMinDimensions(dataUrl, MIN_DIMENSION, MIN_DIMENSION);

            this.setState({
                file,
                src: dataUrl,
                imgSrc: dataUrl,
                croppedImageUrl: null,
                blob: null,
                error: "",
                cropOpen: true,
            });
        } catch (err) {
            const msg = err?.message || "Invalid image selected.";
            this.setState({ error: msg, src: null, imgSrc: "", cropOpen: false });
            this.props.dispatch(
                notificationMessageActions.addNotificationToQueue(msg, 2)
            );
        }
    };

    onImageLoad = (e) => {
        const img = e.currentTarget;
        const width = img.naturalWidth;
        const height = img.naturalHeight;

        // make initial % width large enough to satisfy MIN_DIMENSION in px
        const cropWidthPct = Math.min(100, Math.max((MIN_DIMENSION / width) * 100, 1));

        const base = makeAspectCrop(
            { unit: "%", width: cropWidthPct },
            ASPECT_RATIO,
            width,
            height
        );
        const centered = centerCrop(base, width, height);
        this.setState({ crop: centered });
    };

    performCrop = async () => {
        const img = this.imgRef.current;
        const canvas = this.previewCanvasRef.current;
        const { crop } = this.state;
        if (!img || !canvas || !crop) return;

        // Use NATURAL dimensions for pixel crop
        const pixelCrop = convertToPixelCrop(
            crop,
            img.naturalWidth,
            img.naturalHeight
        );

        try {
            // draw (DPR aware, via setCanvasPreview)
            setCanvasPreview(img, canvas, pixelCrop /*, { backgroundColor: "#fff" }*/);

            // export result
            const dataUrl = canvas.toDataURL("image/png");
            const blob = await this.canvasToBlob(canvas, "image/png", 0.92);

            // store and update preview immediately
            this.setState(
                { croppedImageUrl: dataUrl, blob },
                () => this.handleImageChange() // auto-save into preview + notify parent (matches your original flow)
            );
        } catch (e) {
            console.error("Crop failed:", e);
            const dataUrl = canvas.toDataURL("image/png");
            this.setState({ croppedImageUrl: dataUrl, blob: null }, () => this.handleImageChange());
        }
    };

    handleImageChange = () => {
        const { blob, croppedImageUrl } = this.state;
        if (!croppedImageUrl) return;

        // Update main preview and notify parent
        this.setState(
            {
                imagePreviewUrl: croppedImageUrl,
                src: null,
                imgSrc: "",
                cropOpen: false,
            },
            () => {
                this.props.callback?.({ state: this.state, newImage: true });
            }
        );
    };

    render() {
        const {
            crop,
            croppedImageUrl,
            imgSrc,
            imagePreviewUrl,
            error,
        } = this.state;

        const prefersDark = localStorage.getItem("darkMode") === "true";
        const isPlaceholder = [defaultImage, defaultAvatar].includes(imagePreviewUrl);
        const hasImage = !!croppedImageUrl || (!!imagePreviewUrl && !isPlaceholder);
        const isEditing = !!imgSrc && !croppedImageUrl; // cropping flow is open with a picked file
        return (
            <div className="fileinput text-center full-width" style={{ display: "block" }}>
                {/* Hidden file input */}
                <input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={this.onSelectFile}
                    ref={this.fileInputRef}
                    style={{ display: "none" }}
                />

                {/* Preview */}
                <div>
                    <Col className="ml-auto mr-auto text-center">
                        <span className="mask opacity-8 blur"
                            style={{
                                backgroundImage: `url(${!croppedImageUrl ? imagePreviewUrl : croppedImageUrl})`,
                            }}
                        />
                        <Col xs={12} sm={12} md={12} lg={12} xl={12} className="ml-auto mr-auto px-0">
                            <CardImg
                                alt="..."
                                className="cursor-pointer"
                                onClick={this.handleClick}
                                src={!croppedImageUrl ? imagePreviewUrl : croppedImageUrl}
                                onError={(e) => { e.target.onerror = null; e.target.src = defaultImage }}
                                style={{
                                    maxWidth: "500px",
                                    maxHeight: "500px",
                                    zIndex: '99',
                                    borderRadius: '0.375rem',
                                    objectFit: "cover"
                                }}
                                top
                            />
                        </Col>
                    </Col>
                </div>
                {/* <div className="event-image-container">
          {!croppedImageUrl && (
            <img className="event-image" src={imagePreviewUrl} alt="Event" />
          )}
          {croppedImageUrl && (
            <img className="event-image" alt="Crop" src={croppedImageUrl} />
          )}
        </div> */}

                {/* Error (optional) */}
                {error ? <div className="text-danger small mt-2">{error}</div> : null}

                {/* Cropper */}
                <Modal
                    isOpen={imgSrc && !croppedImageUrl}
                    className="modal-dialog-centered modal-dark "
                    contentClassName={"bg-transparent-carbon"}
                    fade={false}
                >
                    <ModalHeader toggle={this.closeModal}>
                        Crop Image to Size
                    </ModalHeader>
                    {imgSrc && !croppedImageUrl && (
                        <div className="text-center mb-3">
                            <ReactCrop
                                crop={crop}
                                onChange={(_, percentCrop) => this.setState({ crop: percentCrop })}
                                keepSelection
                                aspect={ASPECT_RATIO}
                                minWidth={MIN_DIMENSION}
                            >
                                <img
                                    ref={this.imgRef}
                                    src={imgSrc}
                                    alt="Upload"
                                    style={{ maxHeight: "70vh" }}
                                    onLoad={this.onImageLoad}
                                />
                            </ReactCrop>

                            <Row className="text-center mx-auto pt-2">
                                <Button
                                    size="sm"
                                    color="success"
                                    className="mx-auto"
                                    onClick={this.performCrop}
                                >
                                    Confirm Image
                                </Button>
                            </Row>

                            {crop && (
                                <canvas
                                    ref={this.previewCanvasRef}
                                    className="mt-4"
                                    style={{
                                        display: "none",
                                        border: "1px solid black",
                                        objectFit: "contain",
                                        width: 150,
                                        height: 150,
                                    }}
                                />
                            )}
                        </div>
                    )}
                </Modal>
            </div>
        );
    }
}

export default SquareProfileImageUpload;